import {
  CertificateElement,
  CertificatePageSettings,
  CertificateTemplate,
} from "../certificatePrint/certificateTypes";
import { DEFAULT_CERTIFICATE_LOGO } from "../certificatePrint/certificateTemplates";

const NAVY = "#174d7a";
const INK = "#1e293b";
const MUTED = "#64748b";
const ACCENT = "#e8f4fc";

const PAGE: CertificatePageSettings = {
  size: "A4",
  orientation: "portrait",
  marginMm: 16,
  fontFamily: "Helvetica, Arial, sans-serif",
  fontSize: 10,
  fontWeight: 400,
  lineHeight: 1.4,
  textAlign: "left",
  logoSrc: DEFAULT_CERTIFICATE_LOGO,
  logoSizeMm: 26,
  letterheadSrc: "",
  fieldSpacing: 2,
  paragraphSpacing: 3,
  labelValueStyle: "inline",
  showSafeArea: false,
  headerAlign: "left",
  footerText: "Confidential patient report",
};

const text = (
  partial: Omit<
    CertificateElement,
    "type" | "visible" | "lineHeight" | "align" | "fontWeight" | "fontSize"
  > & {
    visible?: boolean;
    lineHeight?: number;
    align?: CertificateElement["align"];
    fontWeight?: number;
    fontSize?: number;
  }
): CertificateElement => ({
  type: "text",
  visible: true,
  lineHeight: 1.4,
  align: "left",
  fontWeight: 400,
  fontSize: 9,
  color: INK,
  ...partial,
});

const table = (
  partial: Parameters<typeof text>[0]
): CertificateElement => ({
  ...text(partial),
  type: "table",
});

const chart = (
  partial: Parameters<typeof text>[0]
): CertificateElement => ({
  ...text(partial),
  type: "chart",
});

const checklist = (
  partial: Parameters<typeof text>[0]
): CertificateElement => ({
  ...text(partial),
  type: "checklist",
});

function bar(id: string, y: number, label: string): CertificateElement {
  return {
    id,
    type: "text",
    label,
    section: "header",
    x: 16,
    y,
    width: 178,
    height: 1.2,
    content: " ",
    visible: true,
    fontSize: 6,
    fontWeight: 400,
    align: "left",
    lineHeight: 1,
    backgroundColor: NAVY,
    color: NAVY,
  };
}

function standardElements(): CertificateElement[] {
  return [
    {
      id: "letterhead",
      type: "logo",
      label: "Letterhead",
      section: "header",
      x: 16,
      y: 6,
      width: 178,
      height: 28,
      content: "",
      visible: false,
      fontSize: 10,
      fontWeight: 400,
      align: "center",
      lineHeight: 1,
      fieldKey: "letterhead",
    },
    {
      id: "logo",
      type: "logo",
      label: "Clinic logo",
      section: "header",
      x: 168,
      y: 10,
      width: 26,
      height: 26,
      content: "",
      visible: true,
      fontSize: 12,
      fontWeight: 400,
      align: "center",
      lineHeight: 1,
    },
    text({
      id: "clinicName",
      label: "Clinic name",
      section: "header",
      x: 16,
      y: 10,
      width: 146,
      height: 8,
      content: "{{clinicName}}",
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: 0.3,
      textTransform: "uppercase",
      color: NAVY,
      fieldKey: "clinicName",
    }),
    text({
      id: "clinicAddress",
      label: "Clinic address",
      section: "header",
      x: 16,
      y: 19,
      width: 146,
      height: 5,
      content: "{{clinicAddress}}",
      fontSize: 9,
      fontWeight: 400,
      color: MUTED,
      fieldKey: "clinicAddress",
    }),
    text({
      id: "clinicPhone",
      label: "Clinic phone",
      section: "header",
      x: 16,
      y: 25,
      width: 110,
      height: 5,
      content: "Phone: {{clinicPhone}}",
      fontSize: 9,
      fontWeight: 400,
      color: MUTED,
      fieldKey: "clinicPhone",
    }),
    text({
      id: "generatedAt",
      label: "Generated time",
      section: "header",
      x: 126,
      y: 25,
      width: 36,
      height: 5,
      content: "{{generatedAt}}",
      fontSize: 8,
      fontWeight: 400,
      align: "right",
      color: MUTED,
      fieldKey: "generatedAt",
    }),
    bar("headerRule", 33, "Header line"),
    text({
      id: "reportTitle",
      label: "Report title",
      section: "body",
      x: 16,
      y: 37,
      width: 178,
      height: 8,
      content: "PATIENT REPORT",
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: 1.4,
      color: NAVY,
    }),
    text({
      id: "patientCardBg",
      label: "Patient card",
      section: "body",
      x: 16,
      y: 47,
      width: 178,
      height: 28,
      content: " ",
      fontSize: 6,
      backgroundColor: ACCENT,
      color: ACCENT,
    }),
    text({
      id: "patientName",
      label: "Patient name",
      section: "body",
      x: 19,
      y: 50,
      width: 92,
      height: 6,
      content: "Name: {{patientName}}",
      fontSize: 10,
      fontWeight: 700,
      fieldKey: "patientName",
    }),
    text({
      id: "patientGender",
      label: "Gender",
      section: "body",
      x: 114,
      y: 50,
      width: 38,
      height: 6,
      content: "Gender: {{patientGender}}",
      fontSize: 9,
      fieldKey: "patientGender",
    }),
    text({
      id: "patientAge",
      label: "Age",
      section: "body",
      x: 154,
      y: 50,
      width: 36,
      height: 6,
      content: "Age: {{patientAge}}",
      fontSize: 9,
      fieldKey: "patientAge",
    }),
    text({
      id: "patientReg",
      label: "Reg No",
      section: "body",
      x: 19,
      y: 58,
      width: 92,
      height: 6,
      content: "Reg No: {{patientReg}}",
      fontSize: 9,
      fieldKey: "patientReg",
    }),
    text({
      id: "patientMobile",
      label: "Mobile",
      section: "body",
      x: 114,
      y: 58,
      width: 76,
      height: 6,
      content: "Mobile: {{patientMobile}}",
      fontSize: 9,
      fieldKey: "patientMobile",
    }),
    text({
      id: "patientDob",
      label: "Date of birth",
      section: "body",
      x: 19,
      y: 66,
      width: 92,
      height: 6,
      content: "DOB: {{patientDob}}",
      fontSize: 9,
      fieldKey: "patientDob",
    }),
    text({
      id: "patientAddress",
      label: "Address",
      section: "body",
      x: 114,
      y: 66,
      width: 76,
      height: 6,
      content: "Address: {{patientAddress}}",
      fontSize: 9,
      fieldKey: "patientAddress",
    }),
    text({
      id: "section-1-title",
      label: "Complain title",
      section: "body",
      x: 16,
      y: 80,
      width: 178,
      height: 6,
      content: "Patient complain",
      fontSize: 11,
      fontWeight: 700,
      color: NAVY,
    }),
    table({
      id: "section-1",
      label: "Patient complain",
      section: "body",
      x: 16,
      y: 87,
      width: 178,
      height: 18,
      content: "",
      fieldKey: "complainTable",
    }),
    text({
      id: "section-3-title",
      label: "Diagnosis chart title",
      section: "body",
      x: 16,
      y: 104,
      width: 178,
      height: 6,
      content: "Diagnosis chart",
      fontSize: 11,
      fontWeight: 700,
      color: NAVY,
    }),
    chart({
      id: "section-3",
      label: "Diagnosis Chart",
      section: "body",
      x: 16,
      y: 110,
      width: 178,
      height: 40,
      content: "",
      fieldKey: "diagnosisChart",
    }),
    text({
      id: "section-4-title",
      label: "Treatments title",
      section: "body",
      x: 16,
      y: 153,
      width: 178,
      height: 6,
      content: "Treatments",
      fontSize: 11,
      fontWeight: 700,
      color: NAVY,
    }),
    table({
      id: "section-4",
      label: "Treatments",
      section: "body",
      x: 16,
      y: 159,
      width: 178,
      height: 12,
      content: "",
      fieldKey: "treatmentTable",
    }),
    text({
      id: "section-8-title",
      label: "Prescription title",
      section: "body",
      x: 16,
      y: 174,
      width: 178,
      height: 6,
      content: "Prescription",
      fontSize: 11,
      fontWeight: 700,
      color: NAVY,
    }),
    table({
      id: "section-8",
      label: "Prescription",
      section: "body",
      x: 16,
      y: 180,
      width: 178,
      height: 12,
      content: "",
      fieldKey: "prescriptionTable",
    }),
    text({
      id: "section-5-title",
      label: "Payments title",
      section: "body",
      x: 16,
      y: 195,
      width: 178,
      height: 6,
      content: "Payments",
      fontSize: 11,
      fontWeight: 700,
      color: NAVY,
    }),
    table({
      id: "section-5",
      label: "Payments",
      section: "body",
      x: 16,
      y: 201,
      width: 178,
      height: 11,
      content: "",
      fieldKey: "paymentTable",
    }),
    text({
      id: "section-6-title",
      label: "Medical history title",
      section: "body",
      x: 16,
      y: 215,
      width: 178,
      height: 6,
      content: "Medical history",
      fontSize: 11,
      fontWeight: 700,
      color: NAVY,
    }),
    checklist({
      id: "section-6",
      label: "Medical History",
      section: "body",
      x: 16,
      y: 221,
      width: 178,
      height: 28,
      content: "",
      fieldKey: "medicalHistoryChecklist",
    }),
    text({
      id: "section-2-title",
      label: "Investigation title",
      section: "body",
      x: 16,
      y: 214,
      width: 178,
      height: 6,
      content: "Investigation",
      fontSize: 11,
      fontWeight: 700,
      color: NAVY,
      visible: false,
    }),
    table({
      id: "section-2",
      label: "Investigation",
      section: "body",
      x: 16,
      y: 221,
      width: 178,
      height: 22,
      content: "",
      visible: false,
      fieldKey: "investigationTable",
    }),
    table({
      id: "section-7",
      label: "Diagnosis table",
      section: "body",
      x: 16,
      y: 214,
      width: 178,
      height: 22,
      content: "",
      visible: false,
      fieldKey: "diagnoseTable",
    }),
    text({
      id: "reportDescriptionTitle",
      label: "Description title",
      section: "body",
      x: 16,
      y: 252,
      width: 110,
      height: 5,
      content: "Description",
      fontSize: 11,
      fontWeight: 700,
      color: NAVY,
    }),
    text({
      id: "reportDescription",
      label: "Description",
      section: "body",
      x: 16,
      y: 258,
      width: 110,
      height: 14,
      content: " ",
      fontSize: 9,
      backgroundColor: "#ffffff",
      color: MUTED,
    }),
    text({
      id: "signature",
      label: "Signature",
      section: "footer",
      x: 134,
      y: 258,
      width: 60,
      height: 14,
      content: "Signature\n____________________",
      fontSize: 9,
      fontWeight: 700,
      align: "right",
      lineHeight: 1.6,
      color: INK,
    }),
    bar("footerRule", 276, "Footer line"),
    text({
      id: "reportFooter",
      label: "Footer",
      section: "footer",
      x: 16,
      y: 279,
      width: 110,
      height: 8,
      content: "{{clinicName}}",
      fontSize: 8,
      fontWeight: 600,
      color: NAVY,
    }),
    text({
      id: "reportPage",
      label: "Page number",
      section: "footer",
      x: 128,
      y: 279,
      width: 66,
      height: 8,
      content: "Confidential  ·  Page 1",
      fontSize: 8,
      align: "right",
      color: MUTED,
    }),
  ];
}

function scaleToA5(el: CertificateElement): CertificateElement {
  return {
    ...el,
    x: Math.round((el.x * 148) / 210),
    y: Math.round((el.y * 210) / 297),
    width:
      el.id === "logo"
        ? 18
        : el.id === "letterhead"
          ? Math.max(120, Math.round((el.width * 148) / 210))
          : Math.max(8, Math.round((el.width * 148) / 210)),
    height:
      el.id === "letterhead"
        ? Math.max(18, Math.round((el.height * 210) / 297 * 10) / 10)
        : Math.max(el.height < 2 ? 1 : 5, Math.round((el.height * 210) / 297 * 10) / 10),
  };
}

export const DEFAULT_PATIENT_REPORT_TEMPLATES: CertificateTemplate[] = [
  {
    id: "standard-patient-report",
    name: "Standard Patient Report",
    page: { ...PAGE },
    elements: standardElements(),
  },
  {
    id: "a5-patient-report",
    name: "A5 Patient Report",
    page: { ...PAGE, size: "A5", marginMm: 10, fontSize: 8 },
    elements: standardElements().map(scaleToA5),
  },
];

export const DEFAULT_PATIENT_REPORT_TEMPLATE_ID =
  DEFAULT_PATIENT_REPORT_TEMPLATES[0].id;

export function getDefaultPatientReportTemplateById(
  id: string
): CertificateTemplate | undefined {
  const found = DEFAULT_PATIENT_REPORT_TEMPLATES.find((item) => item.id === id);
  if (!found) return undefined;
  return {
    id: found.id,
    name: found.name,
    page: { ...found.page },
    pageCount: found.pageCount || 1,
    elements: found.elements.map((el) => ({ ...el })),
  };
}
